import React, { useState, useEffect } from 'react'
import {
  ArrowLeft,
  Save,
  RotateCcw
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import assetAssignmentService from '../../services/assetAssignmentService'
import assetAcquisitionService from '../../services/assetAcquisitionService'
import assetDropdownService, { ASSET_DROPDOWN_FALLBACKS, optionsFromDropdown } from '../../services/assetDropdownService'
import { toDateInputValue } from './assetUtils'
import { labelStyle, inputStyle, textareaStyle, gridStyle, sectionStyle, fullWidthStyle, requiredStyle } from './assetFormStyles'

const emptyForm = {
  assignmentCode: '',
  acquisition: '',
  assetTag: '',
  assetName: '',
  assignedToName: '',
  assignedToEmail: '',
  department: '',
  location: '',
  assignmentDate: '',
  returnDate: '',
  status: 'Assigned',
  notes: ''
}

const AssetAssignmentForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const assignmentData = location.state?.assignmentData
  const recordId = searchParams.get('id') || assignmentData?._id

  const [formData, setFormData] = useState(emptyForm)
  const [acquisitions, setAcquisitions] = useState([])
  const [locations, setLocations] = useState([])
  const [statusOptions, setStatusOptions] = useState(optionsFromDropdown(null, 'assignmentStatus', ASSET_DROPDOWN_FALLBACKS.assignmentStatus))
  const [departmentOptions, setDepartmentOptions] = useState(optionsFromDropdown(null, 'department', ASSET_DROPDOWN_FALLBACKS.department))
  const [isLoadingCode, setIsLoadingCode] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  // Fetch next assignment code (only for new records)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchCode = async () => {
        try {
          setIsLoadingCode(true)
          const response = await assetAssignmentService.getNextAssignmentCode()
          if (response.success && response.data) {
            setFormData(prev => ({ ...prev, assignmentCode: response.data.assignmentCode || 'AASN0001' }))
          }
        } catch (error) {
          console.error('Error fetching assignment code:', error)
          setFormData(prev => ({ ...prev, assignmentCode: 'AASN0001' }))
        } finally {
          setIsLoadingCode(false)
        }
      }
      fetchCode()
    } else {
      setIsLoadingCode(false)
    }
  }, [isEditMode, isViewMode])

  useEffect(() => {
    loadDropdowns()
  }, [])

  useEffect(() => {
    if ((isEditMode || isViewMode) && recordId) {
      loadAssignmentData(recordId)
    }
  }, [isEditMode, isViewMode, recordId])

  const loadDropdowns = async () => {
    try {
      const [acquisitionsResponse, dropdownResponse] = await Promise.all([
        assetAcquisitionService.getAllAcquisitions({ limit: 1000 }),
        assetDropdownService.getOptions()
      ])

      if (acquisitionsResponse.success) setAcquisitions(acquisitionsResponse.data || [])
      if (dropdownResponse.success && dropdownResponse.data) {
        setLocations(dropdownResponse.data.locations || [])
        setStatusOptions(optionsFromDropdown(dropdownResponse.data.dropdowns, 'assignmentStatus', ASSET_DROPDOWN_FALLBACKS.assignmentStatus))
        setDepartmentOptions(optionsFromDropdown(dropdownResponse.data.dropdowns, 'department', ASSET_DROPDOWN_FALLBACKS.department))
      }
    } catch (error) {
      console.error('Error loading assignment dropdown data:', error)
      if (!error._errorHandled) toast.error('Failed to load dropdown options')
    }
  }

  const loadAssignmentData = async (id) => {
    try {
      setIsLoading(true)
      const response = await assetAssignmentService.getAssignmentById(id)
      if (response.success && response.data) {
        const record = response.data
        setFormData({
          assignmentCode: record.assignmentCode || '',
          acquisition: record.acquisition?._id || record.acquisition || '',
          assetTag: record.assetTag || '',
          assetName: record.assetName || '',
          assignedToName: record.assignedToName || '',
          assignedToEmail: record.assignedToEmail || '',
          department: record.department || '',
          location: record.location?._id || record.location || '',
          assignmentDate: toDateInputValue(record.assignmentDate),
          returnDate: toDateInputValue(record.returnDate),
          status: record.status || 'Assigned',
          notes: record.notes || ''
        })
      }
    } catch (error) {
      console.error('Error loading assignment data:', error)
      toast.error('Failed to load assignment data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target

    // Selecting an acquisition copies its tag and name onto the assignment
    if (name === 'acquisition') {
      const selected = acquisitions.find(item => item._id === value)
      setFormData(prev => ({
        ...prev,
        acquisition: value,
        assetTag: selected?.assetTag || prev.assetTag,
        assetName: selected?.assetName || prev.assetName
      }))
      return
    }

    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSave = async () => {
    try {
      if (!formData.assignedToName) {
        toast.error('Please fill in Assigned To Name')
        return
      }

      setIsLoading(true)

      const submitData = {
        ...formData,
        acquisition: formData.acquisition || null,
        location: formData.location || null,
        assignmentDate: formData.assignmentDate || null,
        returnDate: formData.returnDate || null
      }

      if (isEditMode && recordId) {
        const response = await assetAssignmentService.updateAssignment(recordId, submitData)
        if (response.success) {
          toast.success('Assignment updated successfully!')
          navigate('/assets/assignments')
        } else {
          toast.error(response.message || 'Failed to update assignment')
        }
      } else {
        const response = await assetAssignmentService.createAssignment(submitData)
        if (response.success) {
          toast.success('Assignment created successfully!')
          navigate('/assets/assignments')
        } else {
          toast.error(response.message || 'Failed to create assignment')
        }
      }
    } catch (error) {
      console.error('Error saving assignment:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else if (!error._errorHandled) {
        toast.error('Failed to save assignment')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if ((isEditMode || isViewMode) && recordId) {
      loadAssignmentData(recordId)
    } else {
      setFormData({ ...emptyForm, assignmentCode: formData.assignmentCode })
    }
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Assets', path: '/assets' },
          { label: 'Assignments', path: '/assets/assignments' },
          { label: isEditMode ? 'Edit' : isViewMode ? 'View' : 'New', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Assignment' : isViewMode ? 'View Assignment' : 'New Assignment'}</h1>
          <p>{isViewMode ? 'View assignment details' : isEditMode ? 'Update assignment information' : 'Assign an asset to a person'}</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/assets/assignments')}>
            <ArrowLeft size={16} />
            Back
          </button>
          {!isViewMode && (
            <>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleReset}>
                <RotateCcw size={16} />
                Reset
              </button>
              <button type="button" className="btn-primary" onClick={handleSave} disabled={isLoading}>
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Assignment Entry</h2>
        </div>
        <form className="form-content">
          <div className="section-header">
            <h3>Asset Details</h3>
          </div>
          <div style={sectionStyle}>
            <div style={gridStyle}>
              <div>
                <label style={labelStyle}>
                  Assignment Code <span style={requiredStyle}>*</span>
                </label>
                <input
                  type="text"
                  name="assignmentCode"
                  value={formData.assignmentCode}
                  onChange={handleInputChange}
                  disabled={isViewMode || isEditMode || isLoadingCode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Acquisition</label>
                <select
                  name="acquisition"
                  value={formData.acquisition}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  <option value="">Select Acquisition</option>
                  {acquisitions.map(item => (
                    <option key={item._id} value={item._id}>
                      {item.assetName} ({item.acquisitionCode})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Asset Name</label>
                <input
                  type="text"
                  name="assetName"
                  value={formData.assetName}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Asset Tag</label>
                <input
                  type="text"
                  name="assetTag"
                  value={formData.assetTag}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Assignee</h3>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={gridStyle}>
              <div>
                <label style={labelStyle}>
                  Assigned To <span style={requiredStyle}>*</span>
                </label>
                <input
                  type="text"
                  name="assignedToName"
                  value={formData.assignedToName}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  required
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Assignee Email</label>
                <input
                  type="email"
                  name="assignedToEmail"
                  value={formData.assignedToEmail}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Department</label>
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  <option value="">Select Department</option>
                  {departmentOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Location</label>
                <select
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  <option value="">Select Location</option>
                  {locations.map(loc => (
                    <option key={loc._id} value={loc._id}>
                      {loc.locationName} ({loc.locationCode})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Assignment Period</h3>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={gridStyle}>
              <div>
                <label style={labelStyle}>Assignment Date</label>
                <input
                  type="date"
                  name="assignmentDate"
                  value={formData.assignmentDate}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Return Date</label>
                <input
                  type="date"
                  name="returnDate"
                  value={formData.returnDate}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Additional Information</h3>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={fullWidthStyle}>
              <label style={labelStyle}>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                disabled={isViewMode}
                rows={4}
                style={textareaStyle}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AssetAssignmentForm
